import type { FramedProduct } from '@rpl/pricing';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import type { Rotation } from '../../../lib/rotate';
import type { BwStyle, CropRect } from '../../../lib/types';
import { FramePreview } from './FramePreview';

/**
 * The 3D framed preview — a self-rotating ±sweep with drag-to-rotate and the
 * travelling glass glare, driving --fpv3d-angle/--fpv3d-glare via rAF (see
 * cart.css .fpv3d). Extracted from FramePreviewModes (owner 2026-07-26) so the
 * Adjustments editor can show the SAME 3D view without the Normal/In-room
 * tabs. Live-mode props (cropRect/rotation/bw/fit) pass straight through to
 * FramePreview, so the editor's crop/rotate/B&W render inside the turning
 * frame. Mounting starts the sway fresh; unmounting stops it.
 */

/* Sway + drag tuning (owner round 4: the frame must visibly rotate BY ITSELF;
   a horizontal drag turns it further, then the sway resumes from the released
   angle). rAF instead of CSS keyframes precisely so pause/resume can happen
   at any angle without a jump. */
const SWAY_AMP_DEG = 22; // self-rotation sweep: +22° … −22° and back
const SWAY_PERIOD_MS = 9000; // one full out-and-back
const STATIC_DEG = 18; // prefers-reduced-motion: fixed turned pose
const DRAG_DEG_PER_PX = 0.35;
const DRAG_MAX_DEG = 35; // ≈70° total range (owner round 3)
const DRAG_START_PX = 6; // hijack threshold — taps/scrolls never stop the sway
/** Per-frame easing toward the sway path — also smooths the post-drag handoff. */
const SWAY_EASE = 0.08;

export function Frame3DView({
  photo,
  option,
  cropRect,
  rotation,
  bw,
  bwStyle = null,
  bwStrength = null,
  fit,
}: {
  photo: { previewUrl: string | null; width: number; height: number } | undefined;
  option: FramedProduct;
  /** Live crop rect (rotated-image space); omit for the plain centred render. */
  cropRect?: CropRect | null;
  rotation?: Rotation;
  bw?: boolean;
  bwStyle?: BwStyle | null;
  bwStrength?: number | null;
  fit?: boolean;
}) {
  const [dragging, setDragging] = useState(false);

  const stageRef = useRef<HTMLDivElement>(null);
  /** The currently painted angle — the drag base and the easing state. */
  const angleRef = useRef(SWAY_AMP_DEG);
  /** Sway clock: angle target = AMP · cos(phase + elapsed/period · 2π). */
  const swayRef = useRef({ phase: 0, since: 0 });
  const dragRef = useRef<{ startX: number; base: number; active: boolean } | null>(null);

  /** Paint an angle: the rotation itself + the matching glass-glare position. */
  const applyAngle = useCallback((deg: number) => {
    const el = stageRef.current;
    if (!el) return;
    angleRef.current = deg;
    el.style.setProperty('--fpv3d-angle', `${deg}deg`);
    // Glare sweeps across the glass as the frame turns (+AMP → 0%, −AMP → 100%).
    const glare = Math.min(100, Math.max(0, 50 - (deg / SWAY_AMP_DEG) * 50));
    el.style.setProperty('--fpv3d-glare', `${glare}%`);
  }, []);

  // Self-rotation driver. Starts on the fully-turned pose (depth reads at
  // first glance), eases along the sweep, pauses while a drag holds the
  // pointer and resumes from wherever the customer lets go.
  useEffect(() => {
    angleRef.current = SWAY_AMP_DEG;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      applyAngle(STATIC_DEG);
      return;
    }
    swayRef.current = { phase: 0, since: performance.now() };
    applyAngle(SWAY_AMP_DEG);
    let raf = requestAnimationFrame(function tick(now: number) {
      if (!dragRef.current?.active) {
        const { phase, since } = swayRef.current;
        const target =
          SWAY_AMP_DEG * Math.cos(phase + ((now - since) / SWAY_PERIOD_MS) * 2 * Math.PI);
        applyAngle(angleRef.current + (target - angleRef.current) * SWAY_EASE);
      }
      raf = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(raf);
  }, [applyAngle]);

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!e.isPrimary) return;
    // Only ARM the drag — the sway keeps going until a real horizontal pull
    // (round-3 bug: any touch froze the animation for good).
    dragRef.current = { startX: e.clientX, base: angleRef.current, active: false };
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d || !e.isPrimary) return;
    const dx = e.clientX - d.startX;
    if (!d.active) {
      if (Math.abs(dx) < DRAG_START_PX) return;
      d.active = true;
      setDragging(true);
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    applyAngle(Math.max(-DRAG_MAX_DEG, Math.min(DRAG_MAX_DEG, d.base + dx * DRAG_DEG_PER_PX)));
  };
  const onPointerEnd = () => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d?.active) return;
    setDragging(false);
    // Resume the sway from (nearest to) the released angle; the per-frame
    // easing smooths over the clamp gap when the drag went past ±AMP.
    const a = Math.max(-SWAY_AMP_DEG, Math.min(SWAY_AMP_DEG, angleRef.current));
    swayRef.current = { phase: Math.acos(a / SWAY_AMP_DEG), since: performance.now() };
  };

  return (
    <div
      ref={stageRef}
      className={`fpv3d${dragging ? ' fpv3d--dragging' : ''}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      onDragStart={(e) => e.preventDefault()} /* native img drag would steal the pointer */
    >
      <FramePreview
        photo={photo}
        option={option}
        cropRect={cropRect}
        rotation={rotation}
        bw={bw}
        bwStyle={bwStyle}
        bwStrength={bwStrength}
        fit={fit}
      />
    </div>
  );
}
